import { Card, Center, Loader, Stack, Text } from '@mantine/core';
import { GetTuyaDeviceResponse } from 'api/actions/get-tuya-device/get-tuya-device-response';
import { useApi } from 'api/api-context';
import useLang from 'lang';
import panic from 'panic';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import H2SemiBold from 'theme/components/Typography/H2SemiBold';
import P1Regular from 'theme/components/Typography/P1Regular';
import ZigbeePairing from 'pages/tuya-device/ZigbeePairing';
import TuyaThermostatDetail from 'pages/tuya-device/TuyaThermostatDetail';
import { withBrowserTimezone } from 'env/browser-timezone';

type TuyaDevice = GetTuyaDeviceResponse;

/**
 * Placeholder detail implementation for app-visible Tuya devices.
 */
export function TuyaDeviceDetailImpl({ tuyaDeviceId }: { tuyaDeviceId: number }) {
  const { lang, _t } = useLang();
  const { getAction } = useApi();
  const [tuyaDevice, setTuyaDevice] = useState<TuyaDevice | null>(null);

  useEffect(() => {
    if (!tuyaDeviceId) {
      return;
    }

    const getTuyaDevice = getAction('GetTuyaDevice');

    getTuyaDevice({ parameters: { tuyaDeviceId }, query: withBrowserTimezone({}) })
      .then((tuyaDevice) => {
        setTuyaDevice(tuyaDevice);
      })
      .catch(panic);
  }, [tuyaDeviceId]);

  useEffect(() => {
    if (tuyaDevice?.label) {
      document.title = tuyaDevice.label;
    }
  }, [tuyaDevice?.label]);

  if (!tuyaDevice) {
    return (
      <Center style={{ height: '100vh' }}>
        <Stack align="center" gap="1rem">
          <Loader size="lg" color="#551FFF" />
          <H2SemiBold>{_t('Loading device')}</H2SemiBold>
        </Stack>
      </Center>
    );
  }

  if (tuyaDevice.type === 'thermostat' && tuyaDevice.details) {
    return (
      <Stack bg="gray.0" py={20} px={16} mih="100vh">
        <TuyaThermostatDetail device={tuyaDevice} lang={lang} _t={_t} />
      </Stack>
    );
  }

  return (
    <Stack bg="gray.0" py={20} px={16} mih="100vh">
      <Card p={20} radius={10}>
        <Stack gap={12}>
          <H2SemiBold>{tuyaDevice.label || _t('Tuya device')}</H2SemiBold>
          <P1Regular>{_t('Tuya device detail will be available here later.')}</P1Regular>
          <Text c="dimmed" size="sm">
            {_t('Type')}: {tuyaDevice.type}
          </Text>
        </Stack>
      </Card>
      {tuyaDevice.type === 'zigbee-gateway' && (
        <Stack gap={12}>
          <H2SemiBold>{_t('Pair Zigbee sensor')}</H2SemiBold>
          <ZigbeePairing key={tuyaDevice.id} gateway={tuyaDevice} />
        </Stack>
      )}
    </Stack>
  );
}

/**
 * Placeholder detail page for app-visible Tuya devices.
 */
export default function TuyaDeviceDetail() {
  const { tuyaDeviceId: tuyaDeviceIdRaw = '' } = useParams();
  const tuyaDeviceId = useMemo(() => parseInt(tuyaDeviceIdRaw), [tuyaDeviceIdRaw]);

  return <TuyaDeviceDetailImpl tuyaDeviceId={tuyaDeviceId} />;
}
